import axios from '@fs/zion-axios'
import { buildLangUrl } from '@fs/zion-router'
import { getMostRecentFieldValue } from '@fs/zion-sls'
import { getImageMetaData } from '@fs/zion-image-viewer'
import { getImagePermissions } from '../../../shared/services/platform/platformService'
import { getArtifact } from '../../../shared/services/rms/rmsService'
import { errorCollector } from '../../../shared/utils/utils'
import { normalizeForMatching } from '../../../shared/utils/stringNormalization'

export async function getTextTranscript(url, plainTextTranscript) {
  try {
    const response = await axios.post(
      buildLangUrl(url),
      {
        messages: [
          { role: 'system', content: 'act like a genealogist' },
          {
            role: 'user',
            content: `
      Summarize the following text highlighting key persons, their relationships, and important dates.
      Ensure the summary is 3-4 sentences long. Present it in the following format:
      Summary: [summary]
      Dates:
      - [date1]
      - [date2]
      Names and Relationships:
      - [Name1] [relationship1]
      - [Name2] [relationship2]
      Rewrite the following text to be inoffensive and work with OpenAI's policy.
      ${plainTextTranscript}`,
          },
        ],
      },
      { headers: { Accept: 'text/plain' } }
    )
    return response.data
  } catch (error) {
    console.error('Error:', error)
  }
  return null
}

/**
 * Streams text transcript processing with real-time chunk delivery.
 *
 * This function intentionally uses await in a loop for streaming data processing.
 * Each chunk must be fully processed before proceeding to maintain proper streaming
 * behavior and ensure data integrity. The sequential processing is required for
 * the streaming response to work correctly with the onChunk callback.
 *
 * @param {string} url - The API endpoint URL for text processing
 * @param {string} plainTextTranscript - The raw text to be processed
 * @param {function} onChunk - Callback function called for each streamed chunk
 * @returns {Promise<string>} The complete processed response
 */
export async function getTextTranscriptStreaming(url, plainTextTranscript, onChunk) {
  try {
    const response = await fetch(buildLangUrl(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: [
          { role: 'system', content: 'act like a genealogist' },
          {
            role: 'user',
            content: `
      Summarize the following text highlighting key persons, their relationships, and important dates.
      Ensure the summary is 3-4 sentences long. Present it in the following format:
      Summary: [summary]
      Dates:
      - [date1]
      - [date2]
      Names and Relationships:
      - [Name1] [relationship1]
      - [Name2] [relationship2]
      Rewrite the following text to be inoffensive and work with OpenAI's policy.
      ${plainTextTranscript}`,
          },
        ],
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
    }

    if (!response.body) {
      throw new Error('Response body is null or undefined')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let fullResponse = ''

    try {
      let done = false
      while (!done) {
        // The use of 'await' in this loop is intentional and necessary for streaming.
        // We must wait for each chunk from the stream before proceeding to the next
        // iteration.
        // eslint-disable-next-line no-await-in-loop
        const result = await reader.read()
        done = result.done
        const value = result.value

        if (value) {
          const chunkText = decoder.decode(value, { stream: true })
          fullResponse += chunkText
          if (onChunk) {
            onChunk(chunkText, fullResponse)
          }
        }
      }

      return fullResponse
    } finally {
      reader.releaseLock()
    }
  } catch (error) {
    errorCollector(error, 'Error in streaming:')
    throw error
  }
}

/**
 * Matches a phrase by comparing normalized word sequences.
 * Extracts words from each field after normalization, skipping fields that
 * normalize to empty strings (e.g., special character fields like "↔").
 *
 * @param {string} keyword - The search phrase (e.g., "francisco y dolores maria")
 * @param {Array} fs - Array of field objects to check
 * @returns {boolean} - True if the normalized word sequence matches
 */
export const matchPhrase = (keyword, fs) => {
  // Extract normalized words from the keyword
  const keywordWords = normalizeForMatching(keyword)
    .split(/\s+/)
    .filter((w) => w.length > 0)

  // Extract normalized words from fields, skipping empty normalizations
  const fieldWords = []
  fs.forEach((f) => {
    const fieldValue = getMostRecentFieldValue(f)
    const normalized = normalizeForMatching(fieldValue)
    // Split and collect non-empty words
    const words = normalized.split(/\s+/).filter((w) => w.length > 0)
    fieldWords.push(...words)
  })

  // Compare word sequences
  if (keywordWords.length !== fieldWords.length) return false
  return keywordWords.every((word, i) => word === fieldWords[i])
}

/**
 * Builds a list of fields that match search keywords, handling both single
 * keywords and multi-word phrases. Normalizes text to handle diacritics and
 * special characters, skipping fields that normalize to empty strings.
 *
 * @param {Array<string>} keywords - Array of search terms (can be single words or phrases)
 * @param {Array} fields - Array of field objects from the transcript
 * @returns {Array} - Array of matching fields marked with isKeyword: true
 */
export function buildKeywordFields(keywords, fields) {
  const keywordFieldS = []
  const seenFields = new Set() // Use Set for O(1) duplicate checking
  if (!fields) return keywordFieldS

  // PERFORMANCE OPTIMIZATION: Precompute normalized values once per field
  // Previously normalized each field O(n) times in phrase matching loops
  // This reduces buildKeywordFields from O(n²) to O(n) complexity
  const fieldNormalizations = new Map() // field → normalized string
  const fieldWords = new Map() // field → normalized word array

  fields.forEach((field) => {
    const fieldValue = getMostRecentFieldValue(field)
    const normalized = normalizeForMatching(fieldValue)

    // Store full normalized string for single keyword matching
    fieldNormalizations.set(field, normalized)

    // Store word array for phrase matching
    const words = normalized.split(/\s+/).filter((w) => w.length > 0)
    fieldWords.set(field, words)
  })
  if (keywords)
    keywords.forEach((keyword) => {
      const words = keyword?.split(' ')
      if (words?.length > 1) {
        // Multi-word phrase matching
        // Extract normalized words from keyword
        const keywordWords = normalizeForMatching(keyword)
          .split(/\s+/)
          .filter((w) => w.length > 0)

        // Try starting at each field position
        for (let startIndex = 0; startIndex < fields.length; startIndex++) {
          const collectedWords = []
          const collectedFields = []
          let currentIndex = startIndex

          // Collect fields until we have enough normalized words
          while (collectedWords.length < keywordWords.length && currentIndex < fields.length) {
            const field = fields[currentIndex]

            // OPTIMIZATION: Use precomputed words (O(1)) instead of re-normalizing (O(m))
            const normalizedWords = fieldWords.get(field)

            // Always collect the field, even if it normalizes to empty (e.g., "↔")
            collectedFields.push(field)
            // But only collect its words if it has any
            collectedWords.push(...normalizedWords)
            currentIndex++
          }

          // Check if collected words match keyword words
          if (
            collectedWords.length === keywordWords.length &&
            collectedWords.every((word, i) => word === keywordWords[i])
          ) {
            // Mark all collected fields (including special char fields)
            collectedFields.forEach((field) => {
              if (!seenFields.has(field)) {
                seenFields.add(field)
                keywordFieldS.push(field)
              }
            })
          }
        }
      } else {
        // Single keyword matching using precomputed normalization
        const normalizedKeyword = normalizeForMatching(keyword)

        fields.forEach((f) => {
          // OPTIMIZATION: Use precomputed value (O(1)) instead of re-normalizing
          const normalizedValue = fieldNormalizations.get(f)

          if (normalizedKeyword === normalizedValue) {
            if (!seenFields.has(f)) {
              seenFields.add(f)
              keywordFieldS.push(f)
            }
          }
        })
      }
    })
  keywordFieldS.forEach((kf) => {
    kf.isKeyword = true
  })
  return keywordFieldS
}

export const fetchSummarization = async ({
  currentImageArk,
  plainTextTranscript,
  cachedSummarizations,
  setSummarizationData,
  setCachedSummarizations,
  setLoading,
  setSummaryError,
  azure41StreamingPromptFlagIsOn,
  parseTranscript,
  hasMeaningfulContent,
  setStreamingContent,
  setIsStreaming,
}) => {
  try {
    if (!plainTextTranscript || !currentImageArk) {
      setSummarizationData(null)
      setLoading(false)
      return
    }

    if (cachedSummarizations[currentImageArk]) {
      setSummarizationData(cachedSummarizations[currentImageArk]) // Use cached data if available
      setLoading(false)
      return
    }

    setLoading(true)

    // Only make the AI request if the transcript has meaningful content
    if (!hasMeaningfulContent(plainTextTranscript)) {
      setSummarizationData(null) // Set transcript data to null if no meaningful content
      setLoading(false)
      return
    }
    let response
    if (azure41StreamingPromptFlagIsOn && setStreamingContent && setIsStreaming) {
      // Use streaming for real-time display - skip loading spinner, show skeleton immediately
      setIsStreaming(true)
      setLoading(false) // Turn off loading immediately so skeleton can show
      response = await getTextTranscriptStreaming(
        '/records/improve/azureAiPrompt41',
        plainTextTranscript,
        (chunk, fullResponse) => {
          setStreamingContent(fullResponse)
        }
      )
      setIsStreaming(false)
      setStreamingContent('') // Clear streaming content once complete
    } else if (azure41StreamingPromptFlagIsOn) {
      // Use the streaming-capable azureAiPrompt41 endpoint (with messages array support)
      response = await getTextTranscript('/records/improve/azureAiPrompt41', plainTextTranscript)
    } else {
      // Fallback to original azureAiPrompt (still uses messages array now due to updated interface)
      response = await getTextTranscript('/records/improve/azureAiPrompt', plainTextTranscript)
    }
    const parsedData = parseTranscript(response?.result || response)

    // Cache the fetched data and update the current transcript data
    setCachedSummarizations((prevTranscripts) => ({
      ...prevTranscripts,
      [currentImageArk]: parsedData,
    }))
    setSummarizationData(parsedData)
    setSummaryError(false)
  } catch (error) {
    setIsStreaming(false)
    setStreamingContent('')
    setSummaryError(true)
    errorCollector(error, currentImageArk, {
      codeId: 29,
    })
  } finally {
    // Always set loading to false at the end, unless we're in streaming mode where it's already false
    if (!azure41StreamingPromptFlagIsOn || !setStreamingContent || !setIsStreaming) {
      setLoading(false)
    }
  }
}

export const getImagePermissionsBoolean = async (partitionKey, dispatch) => {
  if (partitionKey !== undefined) {
    const data = await getImagePermissions(null, partitionKey)
    dispatch({
      type: 'SET_IMAGE_PERMISSION',
      value: data,
    })
  }
}

export const getDownloadPermission = async (imageArk, dispatch) => {
  if (!imageArk) return

  try {
    const metadata = await getImageMetaData(imageArk)
    // Use the same download property that ComboViewer uses
    const downloadAllowed = metadata?.download ?? false

    dispatch({
      type: 'SET_DOWNLOAD_PERMISSION',
      value: downloadAllowed,
    })
  } catch (error) {
    console.error('[FullText] Failed to fetch download permission:', error)
    // Default to restricted on error (fail-safe)
    dispatch({
      type: 'SET_DOWNLOAD_PERMISSION',
      value: false,
    })
  }
}

export const fetchArtifact = async (thumbnails, currentImageArk, setArtifact, setArtifactLoadErr) => {
  const apid = thumbnails?.find((thumb) => thumb.ark === currentImageArk)?.id
  if (apid) {
    try {
      const artifactData = await getArtifact(apid)
      setArtifact(artifactData)
    } catch (err) {
      errorCollector(err, currentImageArk, {
        codeId: 25,
      })
      setArtifactLoadErr(true)
    }
  }
}
